import type { RouteRecordRaw } from "vue-router";

export enum RouteNames {
  Dispatch = "dispatch",
  Projects = "projects",
  ProjectDetail = "project_detail",
  ProjectDetailGoalSettings = "project_detail_goal_settings",
  EstimatorReportDetail = "estimator_report_detail",
  CrossValidationReportDetail = "cross_validation_report_detail",
  ComparisonBuilder = "comparison_builder",
  Comparison = "comparison",
  WorkspaceDetail = "workspace_detail",
  NewWorkspace = "new-workspace",
  ApiKeys = "api-keys",
  Login = "login",
  LoginSuccess = "login_success",
  DeviceToken = "device_token",
  Logout = "logout",
  NotFound = "not_found",
  Crash = "crash",
  Components = "components",
}

export enum ProjectDetailSections {
  Reports = "reports",
  Agents = "agents",
  /** @deprecated Kept for detail route paths; project tab redirects to Reports. */
  EstimatorReports = "estimators",
  /** @deprecated Kept for detail route paths; project tab redirects to Reports. */
  CrossValidationReports = "cross-validations",
  Comparisons = "comparisons",
  Goals = "goals",
}

/** Top-level app route path segments (`/something`); must not match `/:workspacePublicId`. */
export const RESERVED_WORKSPACE_PUBLIC_IDS = new Set([
  "api-keys",
  "components",
  "crash",
  "device",
  "jupyterlite",
  "login",
  "logout",
  "new-workspace",
  "not-found",
  "workspace",
]);

const devOnlyRoutes: RouteRecordRaw[] = import.meta.env.DEV
  ? [
      {
        path: "/components",
        name: RouteNames.Components,
        component: () => import("@/views/components/ComponentsView.vue"),
        meta: {
          requiresAuthentication: false,
        },
      },
    ]
  : [];

export const routes: RouteRecordRaw[] = [
  ...devOnlyRoutes,
  {
    path: "/",
    name: RouteNames.Dispatch,
    component: () => import("@/views/dispatch/DispatchView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/agent-setup",
    redirect: { name: RouteNames.NotFound },
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId",
    name: RouteNames.Projects,
    component: () => import("@/views/projects/ProjectsListView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName",
    redirect: (to) => ({
      name: RouteNames.ProjectDetail,
      params: {
        workspacePublicId: to.params.workspacePublicId as string,
        projectName: to.params.projectName as string,
        section: ProjectDetailSections.Reports,
      },
    }),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName/:section/:itemId?",
    name: RouteNames.ProjectDetail,
    component: () => import("@/views/projects/ProjectDetailView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: `/:workspacePublicId/:projectName/${ProjectDetailSections.EstimatorReports}/:reportId`,
    name: RouteNames.EstimatorReportDetail,
    component: () => import("@/views/reports/EstimatorReportView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: `/:workspacePublicId/:projectName/${ProjectDetailSections.CrossValidationReports}/:reportId`,
    name: RouteNames.CrossValidationReportDetail,
    component: () => import("@/views/reports/CrossValidationReportView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName/comparisons/builder",
    name: RouteNames.ComparisonBuilder,
    component: () => import("@/views/comparison/EstimatorReportComparisonBuilderView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName/comparisons/:comparisonId",
    name: RouteNames.Comparison,
    component: () => import("@/views/comparison/EstimatorReportComparisonView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName/goal-settings",
    name: RouteNames.ProjectDetailGoalSettings,
    redirect: (to) => ({
      name: RouteNames.ProjectDetail,
      params: {
        workspacePublicId: to.params.workspacePublicId as string,
        projectName: to.params.projectName as string,
        section: ProjectDetailSections.Goals,
      },
    }),
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/workspace/:workspaceId",
    name: RouteNames.WorkspaceDetail,
    component: () => import("@/views/workspaces/WorkspaceDetailView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/login",
    name: RouteNames.Login,
    component: () => import("@/views/authentication/LoginView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/login/success",
    name: RouteNames.LoginSuccess,
    component: () => import("@/views/authentication/LoginSuccessView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/device",
    name: RouteNames.DeviceToken,
    component: () => import("@/views/authentication/DeviceTokenView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/logout",
    name: RouteNames.Logout,
    component: () => import("@/views/authentication/LogoutView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/not-found",
    name: RouteNames.NotFound,
    component: () => import("@/views/NotFoundView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/new-workspace",
    name: RouteNames.NewWorkspace,
    component: () => import("@/views/workspaces/NewWorkspaceView.vue"),
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/api-keys",
    name: RouteNames.ApiKeys,
    component: () => import("@/views/ApiKeysView.vue"),
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/crash",
    name: RouteNames.Crash,
    component: () => import("@/views/CrashView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  },
];
